import { useCallback, useEffect, useState } from "react";

// Fast scrolling can produce a stray small delta in the opposite
// direction right after a run of same-direction deltas. Reacting to
// every single event causes the bar to flash open/closed, so we
// accumulate movement and only toggle once it passes a small threshold,
// resetting the accumulator whenever the direction reverses.
const SCROLL_THRESHOLD = 10;

// A fast fling into the bottom of a container triggers elastic
// overscroll (rubber-banding) on trackpads: the container bounces past
// its max scrollTop and springs back, which reads as a burst of
// upward-direction events large enough to clear SCROLL_THRESHOLD on
// their own. We treat "at the bottom" as an authoritative hidden state
// and ignore upward deltas for a short window afterwards so the
// bounce-back doesn't re-show the bar before it settles.
const BOTTOM_BOUNCE_COOLDOWN_MS = 500;

const useAutoHideOnScroll = (context: string | null) => {
  const [isHidden, setIsHidden] = useState(false);

  // The container renders conditionally (e.g. it's absent while the AI
  // suggests context is still null right after a redirect), so a plain
  // ref whose identity never changes wouldn't retrigger the effect below
  // once the node actually shows up. Tracking it in state makes the
  // attach effect rerun exactly when the node appears or disappears.
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const containerRef = useCallback((node: HTMLElement | null) => {
    setContainer(node);
  }, []);

  useEffect(() => {
    setIsHidden(false);
  }, [context]);

  // Scroll happens on whichever descendant the routed page renders as its
  // scroll container, which changes per page/route. Scroll events don't
  // bubble, but attaching the listener in the capture phase on the shared
  // parent lets us observe scrolling from any of them without having to
  // locate the specific scrollable element.
  useEffect(() => {
    const parent = container?.parentElement;
    if (!parent) return;

    let lastScrollTarget: EventTarget | null = null;
    let lastScrollTop = 0;
    let accumulatedDelta = 0;
    let bottomReachedAt = 0;
    let ignoreTopUntil = 0;

    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target || typeof target.scrollTop !== "number") return;

      const { scrollTop, scrollHeight, clientHeight } = target;
      const maxScrollTop = scrollHeight - clientHeight;
      const atBottom = maxScrollTop > 0 && scrollTop >= maxScrollTop - 1;

      if (target !== lastScrollTarget) {
        lastScrollTarget = target;
        lastScrollTop = scrollTop;
        accumulatedDelta = 0;
        bottomReachedAt = atBottom ? Date.now() : 0;
        return;
      }

      const delta = scrollTop - lastScrollTop;
      lastScrollTop = scrollTop;

      if (scrollTop <= 0) {
        if (ignoreTopUntil && Date.now() < ignoreTopUntil) {
          return;
        }
        ignoreTopUntil = 0;
        accumulatedDelta = 0;
        bottomReachedAt = 0;
        setIsHidden(false);
        return;
      }

      if (atBottom) {
        bottomReachedAt = Date.now();
        ignoreTopUntil = Date.now() + BOTTOM_BOUNCE_COOLDOWN_MS;
        accumulatedDelta = 0;
        setIsHidden(true);
        return;
      }

      if (
        bottomReachedAt &&
        Date.now() - bottomReachedAt < BOTTOM_BOUNCE_COOLDOWN_MS
      ) {
        if (delta < 0) return;
        bottomReachedAt = 0;
      }

      if (ignoreTopUntil && Date.now() >= ignoreTopUntil) {
        ignoreTopUntil = 0;
      }

      if (
        (delta > 0 && accumulatedDelta < 0) ||
        (delta < 0 && accumulatedDelta > 0)
      ) {
        accumulatedDelta = 0;
      }
      accumulatedDelta += delta;

      if (accumulatedDelta > SCROLL_THRESHOLD) {
        setIsHidden(true);
      } else if (accumulatedDelta < -SCROLL_THRESHOLD) {
        setIsHidden(false);
      }
    };

    parent.addEventListener("scroll", handleScroll, true);

    return () => parent.removeEventListener("scroll", handleScroll, true);
  }, [container]);

  return { isHidden, containerRef };
};

export default useAutoHideOnScroll;
